import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CellEditingStartedEvent, ColDef, Column, FilterOpenedEvent, FirstDataRenderedEvent, GridOptions, GridReadyEvent, GridSizeChangedEvent, IRowNode, RowNode, FilterModel,  ColumnState} from 'ag-grid-community';
import moment from 'moment';
import { BehaviorSubject, Subject } from "rxjs";
import { GroupHeader, MarketplaceOrder, MarketplaceOrderStatus } from 'src/AngularJS/Goals/Gamification/Models/marketplace-order';
import { CvNumberLocalizePipe } from 'src/app/reusables/pipes/cv-number-localize.pipe';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CustomNoRowsOverlay } from 'src/shared/ag-grid-components/no-row-overlay';
import { PointStoreProductOrderService } from '../../../agent-side/services/point-store-product-order.service';
import { AgGridAngular } from 'ag-grid-angular';

type OrderProductParams<T> = {
  value: T,
  data: {
    _PointStoreProductOrderedDTO: {
      Category: string,
      CompanyID: number,
      Description: string,
      OrderOrigin: number,
      PointStoreOrderID: number,
      PointStoreProductID: number,
      ProductCost: number,
      ProductImageID: number,
      ProductOrderedID: number,
      ProductQuantity: number,
      ProductStatus: number,
      SubCategory: string,
      Title: string,
      Variation: string,
      VariationName: string,
    }
  }
}

@Component({
  selector: 'cv-marketplace-orders-table-component',
  templateUrl: './marketplace-orders-table-component.component.html',
  styleUrls: ['./marketplace-orders-table-component.component.scss']
})
export class MarketplaceOrdersTableComponentComponent implements OnInit {

  @Input() orders: any[] = [];
  @Input() search: string;
  @Input() timeInterval: number;
  @Input() editable: boolean;
  @Input() status: any[];
  @Input() headersNames: GroupHeader[] = [];
  @Output() orderUpdated: EventEmitter<{ newValue: MarketplaceOrder, oldValue: MarketplaceOrder }> = new EventEmitter();
  @Input() selectGroupChange: boolean;
  @Input() MSTOffset: number = 0;

  @ViewChild('marketplaceOrdersTableGrid') agGrid: AgGridAngular;

  pspof = null;
  getDatePosition = null;
  products: any = null;
  productsBS = new BehaviorSubject<any[]>([]);
  originalOrder: any = null;
  grid: GridOptions = null;
  columnLocalization = new Map();
  tableLocalization = new Map();
  availableStatuses = [
    MarketplaceOrderStatus.Processing,
    MarketplaceOrderStatus.InTransit,
    MarketplaceOrderStatus.Shipped,
    MarketplaceOrderStatus.ReadyForPickup,
    MarketplaceOrderStatus.Complete,
    MarketplaceOrderStatus.Cancelled
  ];
  noDataFound: string = 'No Data Found';
  isFilterPopoverOpen: boolean;
  rowHeightChildDetailRow: number = 74;
  baseHeightDetailRow: number = 132;
  defaultRowHeight: number = 300;
  childrensToChangeHeight: number = 3;
  currentIdOrderOnFilterOpen: any;

  isOrderNumberClicked: boolean = false;
  orderNumberPreviousSort: number = 0; //0 => neutral, 1  =>asc, 2 => desc

  currentFilters: FilterModel = {};
  currentSort: ColumnState[] = [];

  @HostListener('click', ['$event']) onClickComponent(e) {
    if (e.target) {
      const target = e.target.closest('.mot-order-id');
      if (target) {
        this.isOrderNumberClicked = true;
      } else {
        this.isOrderNumberClicked = false;
      }
    }
  }

  constructor(
    private datePipe: DatePipe,
    private agGridHelperService: AgGridHelperService,
    private cvNumberLocalizePipe: CvNumberLocalizePipe,
    private cvLocalizationService: CVLocalizationService,
    private elementRef: ElementRef,
    private pointStoreProductOrderService: PointStoreProductOrderService
  ) {
    this.pspof = pointStoreProductOrderService.pointStoreProductOrderObj;
    this.getDatePosition = this.pspof.pointStoreProductAdminOrderFilter.datePosition;
  }

  async ngOnInit() {
    await this.getNotFoundDataMessage();
    await this.loadLocalization();
    this.products = await this.pointStoreProductOrderService.loadPointStoreProduct();
    this.productsBS.next(this.products);
  }

  async loadLocalization() {
    this.columnLocalization = await this.cvLocalizationService.GetSection("Order_Management");
    this.tableLocalization = await this.cvLocalizationService.GetSection("AgGrid");
    this.cvLocalizationService.GetSection("MarketPlace");
    this.setupGrid();
  }

  isGamePrize(data: any){
    if(!data || data?.length !== 1) return false;
    return data[0]._PointStoreProductOrderedDTO?.Category === 'Game rewards' && data[0]._PointStoreProductOrderedDTO?.SubCategory === 'Command center';
  }

  setupGrid(): void {
    this.grid = {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        width: 200,
      },

      accentedSort: true,
      animateRows: true,
      masterDetail: true,
      keepDetailRowsCount: 1,
      suppressCellFocus: true,
      suppressDragLeaveHidesColumns: true,
      suppressScrollOnNewData: true,
      suppressRowTransform: true,
      getRowStyle: params => {
        if (params.node.expanded) {
          return { background: '#B7E4FF' };
        }
      },
      columnDefs: [{
        field: 'PointStoreOrderID',
        headerName: this.getColumnName('Order_Number', 'Order Number'),
        cellRenderer: 'agGroupCellRenderer',
        lockVisible: true,
        filter: false,
        cellClass: 'cell-orderid',
        minWidth: 200,
        headerClass: 'mot-order-id'
      }, {
        field: 'LoginName',
        headerName: this.getColumnName('Ordered_By', 'Ordered By'),
        filter: 'agSetColumnFilter',
        minWidth: 200,
      }, {
        field: 'LoginID',
        headerName: this.getColumnName('Login_ID', 'Agent ID'),
        filter: 'agSetColumnFilter',
        minWidth: 200,
        type: 'rightAligned',
      }, {
        field: 'Group4Name',
        headerName: this.getColumnName("Default", "Default"),
        filter: 'agSetColumnFilter',
        hide: false,
        minWidth: 200,
      }, {
        field: 'Group3Name',
        headerName: this.getColumnName("Default", "Default"),
        filter: 'agSetColumnFilter',
        hide: false,
        minWidth: 200,
      }, {
        field: 'Group2Name',
        headerName: this.getColumnName("Default", "Default"),
        filter: 'agSetColumnFilter',
        hide: false,
        minWidth: 200,
      }, {
        field: 'DateOrdered',
        headerName: this.getColumnName('Order_Date_Ordered', 'Date Ordered'),
        filter: 'agDateColumnFilter',
        cellRenderer: (params) => this.datePipe.transform(params.value, CVAngularDateFormat),
        filterParams: {
          comparator: (filterDate, cellValue) => this.dateComparator(filterDate, cellValue)
        },
        minWidth: 200,
        type: 'rightAligned',
      }, {
        field: 'OrderTotal',
        headerName: this.getColumnName('Total_Coins', 'Total Coins'),
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: any) => {
          if (this.isGamePrize(params.data.ProductsCollection)){
            return `<span class="game-total-coins">${this.getGameName(params.data._PointStoreProductOrderedDTO?.OrderOrigin)}</span>`;
          }
          return this.cvNumberLocalizePipe.transform(params.value);
        },
        type: 'numericColumn',
        minWidth: 200,
      }, {
        field: 'OrderStatus',
        headerName: this.getColumnName('Status', 'Status'),
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: this.availableStatuses,
          formatValue: (status: MarketplaceOrderStatus) => this.getStatusName(status),
        },
        valueFormatter: (params) => this.getStatusName(params.data.OrderStatus),
        filterParams: {
          valueGetter: (params) => this.getStatusName(params.data.OrderStatus)
        },
        comparator: (valueA, valueB) => this.statusComparator(valueA, valueB),
        editable: this.editable,
        filter: 'agSetColumnFilter',
        minWidth: 200,
      }, {
        field: 'DateOrderStatusChanged',
        headerName: this.getColumnName('Status_Date', 'Status Date'),
        filter: 'agDateColumnFilter',
        cellRenderer: (params) => this.datePipe.transform(params.value, window["CVAngularDateTimeFormat"]),
        minWidth: 200,
      }, {
        field: 'Note',
        headerName: this.getColumnName('Notes', 'Notes'),
        filter: 'agTextColumnFilter',
        cellEditor: 'agLargeTextCellEditor',
        editable: this.editable,
        minWidth: 200,
      }],

      detailCellRendererParams: {
        detailGridOptions: {
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            width: 200,
            flex: 1
          },
          suppressCellFocus: true,
          rowHeight: this.rowHeightChildDetailRow,
          localeText: Object.fromEntries(this.tableLocalization.entries()),
          suppressRowTransform: true,
          columnDefs: [{
            field: '_PointStoreProductOrderedDTO.Image',
            headerName: this.getColumnName('Product', 'Product'),
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
              const prodObj = params.data._PointStoreProductOrderedDTO;
              let $productContainer = $('<div>').addClass('d-flex');
              const className = this.isGamePrize([params.data]) ? 'game-product' : '';
              if (!this.isGamePrize([params.data])){
                // product image
                let imageId = prodObj.ProductImageID > 0 ? prodObj.ProductImageID : -15;
                let $img = $('<img>').addClass('mr-3 my-1').css('height', 64).appendTo($productContainer);
                if (prodObj.Variation != null && prodObj.Variation > 0) {
                  let matchedProduct = this.products.find((p) => p.PointStoreProductID == prodObj.PointStoreProductID);
                  if (matchedProduct) {
                    if (matchedProduct.HasMultipleImages && matchedProduct.MultipleImagesData != null) {
                      let variationImageData = matchedProduct.MultipleImagesData.find(mp => mp.PointStoreProductVariationID == prodObj.Variation)
                      if (variationImageData != null && variationImageData.PointStoreProductImageID != null && variationImageData.PointStoreProductImageID > 0)
                        $img.attr('src', `${_BaseURI}/Admin/ImageResizer.aspx?imgid=${variationImageData.PointStoreProductImageID}&w=auto&h=165`);
                      else
                        $img.attr('src', `${_BaseURI}/Admin/ImageResizer.aspx?imgid=${imageId}&w=auto&h=165`);
                    } else
                      $img.attr('src', `${_BaseURI}/Admin/ImageResizer.aspx?imgid=${imageId}&w=auto&h=165`);
                  } else {
                    $img.attr('src', `${_BaseURI}/Admin/ImageResizer.aspx?imgid=${imageId}&w=auto&h=165`);
                  }
                } else
                  $img.attr('src', `${_BaseURI}/Admin/ImageResizer.aspx?imgid=${imageId}&w=auto&h=165`);
  
                $img.on('error', () => $img.attr('src', `${_BaseURI}/Admin/ImageResizer.aspx?imgid=-15&w=auto&h=165`));
              }
               // product name
               $('<span>').addClass(className).text(params.value).appendTo($productContainer);
              return $productContainer[0];
            },
            minWidth: 200,
            cellClass: 'order-admin-details',
            headerClass: 'order-admin-details',
          }, {
            field: '_PointStoreProductOrderedDTO.Title',
            headerName: this.getColumnName('Product_Name', 'Product Name'),
            filter: 'agTextColumnFilter',
            minWidth: 150,
          }, {
            field: '_PointStoreProductOrderedDTO.VariationName',
            headerName: this.getColumnName('Variation', 'Variation'),
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
              if (params.value?.trim()) {
                return `<span>${params.value}</span>`;
              }
              else {
                return "<span>N/A</span>";
              }
            },
          }, {
            field: '_PointStoreProductOrderedDTO.ProductQuantity',
            headerName: this.getColumnName('Quantity', 'Quantity'),
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
            minWidth: 150,
          }, {
            field: '_PointStoreProductOrderedDTO.ProductCost',
            headerName: this.getColumnName('Coins', 'Coins'),
            filter: 'agNumberColumnFilter',
            cellRenderer: (params: OrderProductParams<string | number>) => {
              if (params.value === 0 && this.isGamePrize([params.data]))
                return `<span class="game-total-coins">${this.getGameName(params.data._PointStoreProductOrderedDTO?.OrderOrigin)}</span>`;
              return this.cvNumberLocalizePipe.transform(params.value);
            },
            type: 'numericColumn',
            minWidth: 150,
          }, {
            field: '_PointStoreProductOrderedDTO.ProductStatus',
            headerName: this.getColumnName('Status', 'Status'),
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: (params: any) => ({
              values: this.availableStatuses,
              formatValue: (status: MarketplaceOrderStatus) => this.getStatusName(status),
            }),
            editable: this.editable,
            valueFormatter: (params) => this.getStatusName(params.value),
            filterParams: {
              valueGetter: (params) => this.getStatusName(params.data._PointStoreProductOrderedDTO.ProductStatus)
            },
            comparator: (valueA, valueB) => this.statusComparator(valueA, valueB),
            filter: 'agSetColumnFilter',
          }, {
            field: '_PointStoreProductOrderedDTO.DateProductStatusChanged',
            headerName: this.getColumnName('Status_Date', 'Status Date'),
            filter: 'agDateColumnFilter',
            cellRenderer: (params) => this.datePipe.transform(params.value, window["CVAngularDateTimeFormat"]),
          }],

          onGridReady: (e: GridReadyEvent) => {
            e.api.sizeColumnsToFit();
          },

          onGridSizeChanged: (e: GridSizeChangedEvent) => {
            e.api.sizeColumnsToFit();
          },

          onFirstDataRendered: (param: FirstDataRenderedEvent) => {
            const firstCol: Column = this.agGrid?.api.getColumns()[0];
            const hParam = firstCol.getColDef().headerComponentParams;
            const rowNode: IRowNode = this.agGrid?.api.getRowNode(hParam?.idOrder);
            if(!rowNode) return;
            const { rowDetailNode, childrenLength } = this.getRowDetailNode(rowNode);

            if (this.validateChildrensToChangeHeight(childrenLength)) {
              rowDetailNode.setRowHeight(this.calculateDynamicHeight(childrenLength));
              this.agGrid?.api.onRowHeightChanged();
            }
          },

          onCellEditingStarted: (params: CellEditingStartedEvent) => {
            if (!this.editable)
              return;

            let productOrdered = params.data._PointStoreProductOrderedDTO;
            let order = this.orders.find((o) => o.PointStoreOrderID == productOrdered.PointStoreOrderID);
            if (!order) {
              return;
            }

            this.originalOrder = angular.copy(order);

            if (params.column.getColId() === '_PointStoreProductOrderedDTO.ProductStatus') {
              const rowNode: IRowNode = this.agGrid?.api.getRowNode(params.data._PointStoreProductOrderedDTO.PointStoreOrderID);
              const { rowDetailNode, childrenLength } = this.getRowDetailNode(rowNode);
              if (this.validateChildrensToChangeHeight(childrenLength)) {
                rowDetailNode.setRowHeight(this.defaultRowHeight);
                this.agGrid?.api.onRowHeightChanged();
              }
            }
          },

          onFilterOpened: (params: FilterOpenedEvent) => {
            const colum: any = params.column;
            const hParams: any = colum.getColDef().headerComponentParams;

            const rowNode: IRowNode = this.agGrid?.api.getRowNode(hParams.idOrder);
            const { rowDetailNode, childrenLength } = this.getRowDetailNode(rowNode);

            if (this.validateChildrensToChangeHeight(childrenLength)) {
              rowDetailNode.setRowHeight(this.defaultRowHeight);
              this.agGrid?.api.onRowHeightChanged();
            }
            this.isFilterPopoverOpen = true;
            this.currentIdOrderOnFilterOpen = hParams.idOrder;
          },

          onCellEditingStopped: (params) => {
            this.currentFilters = this.agGrid.api.getFilterModel();
            this.currentSort = this.agGrid.api.getColumnState();

            const pointStoreOrderId = params.data._PointStoreProductOrderedDTO.PointStoreOrderID;

            if (params.column.getColId() === '_PointStoreProductOrderedDTO.ProductStatus') {
              this.agGrid?.api.stopEditing(true);
              params.api.hidePopupMenu();

              const rowNode: IRowNode = this.agGrid?.api.getRowNode(pointStoreOrderId);
              const { rowDetailNode, childrenLength } = this.getRowDetailNode(rowNode);

              if (this.validateChildrensToChangeHeight(childrenLength)) {
                rowDetailNode.setRowHeight(this.calculateDynamicHeight(childrenLength));
                this.agGrid?.api.onRowHeightChanged();
              }
            }

            if (!this.editable) {
              return;
            }


            if (pointStoreOrderId != this.originalOrder?.PointStoreOrderID) {
              return;
            }
            // find the order related to this product
            let productOrdered = params.data._PointStoreProductOrderedDTO;
            let order = this.orders.find((o) => o.PointStoreOrderID == productOrdered.PointStoreOrderID);
            const updatedProd = order.ProductsCollection.find((p) => p._PointStoreProductOrderedDTO.PointStoreProductID == productOrdered.PointStoreProductID
              && p._PointStoreProductOrderedDTO.Variation === productOrdered.Variation);

            updatedProd._Add = false;
            updatedProd._Dirty = true;//means object was modified
            updatedProd._PointStoreProductOrderedDTO.ProductStatus = productOrdered.ProductStatus;

            // get lowest product status (sorted by the order in availableStatuses)
            let ordersSortedByStatus = order.ProductsCollection
              .map(p => p._PointStoreProductOrderedDTO.ProductStatus)
              .sort((a, b) => this.availableStatuses.indexOf(a) - this.availableStatuses.indexOf(b));
            order.OrderStatus = ordersSortedByStatus[0];

            // check for changes
            let originalProductOrdered = this.originalOrder.ProductsCollection.find((p) => p._PointStoreProductOrderedDTO.PointStoreProductID == productOrdered.PointStoreProductID
              && p._PointStoreProductOrderedDTO.Variation === productOrdered.Variation);
            if (order.OrderStatus === this.originalOrder.OrderStatus && originalProductOrdered._PointStoreProductOrderedDTO.ProductStatus === productOrdered.ProductStatus) {
              // no changes, just exit
              this.originalOrder = null;
              return;
            }

            // update on grid
            this.agGrid?.api.applyTransaction({
              update: [this.originalOrder]
            });

            // update on server
            this.orderUpdated.emit({
              newValue: order,
              oldValue: this.originalOrder
            });

            this.originalOrder = null;
          },

        },

        template: (params) => `<div class="h-100"><div class="h-100" ref="eDetailGrid"></div></div>`,

        getDetailRowData: async (params) => {
          const idOrder = params.node.id?.replace('detail_', '');

          if (params.node.detailGridInfo) {
            const modifiedColumnDefs = params.node.detailGridInfo.api.getAllGridColumns().map(function (column) {
              let colDef: ColDef = column.getColDef();
              colDef.headerComponentParams = { idOrder: Number(idOrder) };
              return colDef;
            });

            params.node.detailGridInfo.api.updateGridOptions({ columnDefs: modifiedColumnDefs });

            this.productsBS.subscribe(value => {
              if (params.data.ProductsCollection && this.products) {
                params.successCallback(params.data.ProductsCollection)
              }
            });
          }
        },
      },

      components: {
        customLoadingOverlay: this.agGridHelperService.loadingOverlay,
      },

      rowData: null,
      loadingOverlayComponent: 'customLoadingOverlay',
      noRowsOverlayComponent: CustomNoRowsOverlay,
      noRowsOverlayComponentParams: {
        noRowsMessage: this.noDataFound,
      },

      localeText: Object.fromEntries(this.tableLocalization.entries()),

      isExternalFilterPresent: () => {
        let evalTimeInterval = this.timeInterval == 0 || !!this.timeInterval;
        return !!this.search || evalTimeInterval || !!this.status;
      },

      doesExternalFilterPass: (node) => {
        let item = node.data;
        let match_date = false;
        let match_status = false;

        //match_status = item.OrderStatus in (this.status);
        if (!this.status) {
          //default status
          this.status = [];
        }

        match_status = this.status.some((status: string) => status === item.OrderStatus);

        //node.data
        var position = this.pointStoreProductOrderService.getDatePosition();
        if (position == 0 || position == 1) {
          //30 or 60 days
          match_date = this.daysBetweenDates(this.pointStoreProductOrderService.pointStoreProductOrderObj.pointStoreProductAdminOrderFilter.date[position].value, item.DateOrdered, false, this.MSTOffset);
        } else if (position == 2) {
          //6 months
          match_date = this.daysBetweenDates(this.pointStoreProductOrderService.pointStoreProductOrderObj.pointStoreProductAdminOrderFilter.date[position].value, item.DateOrdered, false, this.MSTOffset);
        } else if (position >= 3) {
          match_date = this.daysBetweenDates(this.pointStoreProductOrderService.pointStoreProductOrderObj.pointStoreProductAdminOrderFilter.date[position].value, item.DateOrdered, true);
        } else if (position == -1) {
          match_date = true;
        }

        if (match_date && match_status) {
          // go through each column value and return as soon as we get a match
          var filterValueLower = this.search.toLowerCase();

          //-- Empty/Whitespace Input --
          if (filterValueLower.trim() == '')
            return true;

          for (let col of this.agGrid?.api.getColumns()) {
            var columnValue = this.agGrid?.api.getValue(col, node);
            if (columnValue && String(columnValue).toLowerCase().indexOf(filterValueLower) >= 0) {
              return true;
            }
          }
          return false;
        }
        return false;
      },

      getRowId: ({ data: order }) => String(order.PointStoreOrderID),

      getRowClass: function (params) {
        if (params.node && params.node.detail) {
          return 'detail-row';
        }
        return '';
      },

      getRowHeight: (params) => {
        const node: IRowNode = params.node;
        const isDetailRow = node.detail;
        // for all rows that are not detail rows, return nothing
        if (!isDetailRow) {
          return undefined;
        }

        const childrenLength = params.data.ProductsCollection?.length;
        //more than 3 childrens return defaul value given by the ag-grid
        if (this.validateChildrensToChangeHeight(childrenLength)) {
          return this.calculateDynamicHeight(childrenLength);
        }
      },

      onGridReady: (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
      },

      onRowGroupOpened: function (params) {
        params.api.redrawRows({
          rowNodes: [params.node]
        })
      },

      onGridSizeChanged: (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
      },

      onCellEditingStarted: (e) => {
        if (!this.editable)
          return;

        this.originalOrder = angular.copy(e.data);
      },

      onCellEditingStopped: (e) => {
        this.currentFilters = this.agGrid.api.getFilterModel();
        this.currentSort = this.agGrid.api.getColumnState();
        
        if (!this.editable)
          return;

        let hasChanges = false;

        // check for order status changes
        if (e.column.getColId() === 'OrderStatus' && e.data.OrderStatus !== this.originalOrder.OrderStatus) {
          e.data.DateOrderStatusChanged = this.datePipe.transform(new Date(), window["CVAngularDateTimeFormat"]);
          // update all products to this status
          e.data.ProductsCollection.forEach((product) => {
            product._PointStoreProductOrderedDTO.ProductStatus = e.data.OrderStatus;
            product._PointStoreProductOrderedDTO.DateProductStatusChanged = this.datePipe.transform(new Date(), window["CVAngularDateTimeFormat"]);
            product._Add = false;
            product._Dirty = true;//means object was modified
          });

          // update on grid
          e.api.applyTransaction({
            update: [this.originalOrder]
          });

          hasChanges = true;
        }

        // check for note changes
        if (e.column.getColId() === "Note" && e.data.Note !== this.originalOrder.Note)
          hasChanges = true;

        if (!hasChanges) {
          // no changes made, we're done
          this.originalOrder = null;
          return;
        }

        // update on server
        this.orderUpdated.emit({
          newValue: e.data,
          oldValue: this.originalOrder
        });

        this.originalOrder = null;
      },

      onRowSelected: (event) => {
        event.node.data.IsChecked = event.node.isSelected();
      },

      onSortChanged: (event) => this.onSortChangedEvent(event),

      onFirstDataRendered: (e) => {
        this.getFilteredRows();
      }
    };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;
    const filterMenu = this.elementRef.nativeElement.querySelector('.ag-popup');

    if (!filterMenu?.contains(targetElement) && this.isFilterPopoverOpen) {

      this.agGrid?.api.stopEditing(true);
      this.agGrid?.api.hidePopupMenu();

      if (this.currentIdOrderOnFilterOpen) {
        const rowNode: IRowNode = this.agGrid?.api.getRowNode(this.currentIdOrderOnFilterOpen);
        const { rowDetailNode, childrenLength } = this.getRowDetailNode(rowNode);

        if (this.validateChildrensToChangeHeight(childrenLength)) {
          rowDetailNode.setRowHeight(this.calculateDynamicHeight(childrenLength));
          this.agGrid?.api.onRowHeightChanged();
        }
      }
      this.isFilterPopoverOpen = false;
    }
  }

  async getNotFoundDataMessage() {
    this.noDataFound = await this.cvLocalizationService.GetLocalText('MarketPlace_Management', 'No_Data_Found', "No Data Found");
  }

  getFilteredRows() {
    this.pspof.dataFiltered = [];
    this.agGrid?.api.forEachNodeAfterFilter((node: RowNode) => {
      this.pspof.dataFiltered.push(node.data);
    });
    this.pspof.pointStoreProductAdminOrderFilter.groupSelectorChanged = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.search != undefined || changes.timeInterval != undefined || changes.status != undefined || changes.selectGroupChange) {
      if (this.grid && this.agGrid?.api) { // notify filter change
        this.agGrid?.api.onFilterChanged();
        //when filter change
        this.getFilteredRows();
      }
    }

    if (changes.orders != undefined && changes.orders.currentValue != null) {
      if (this.grid && this.agGrid?.api) {
        this.agGrid?.api.showLoadingOverlay();

        if (changes.orders.currentValue && changes.orders.currentValue.length > 0) {
          const ordersFormatted = changes.orders.currentValue.map((o) => {
            o.ProductsCollection.map((p) => {
              if (p._PointStoreProductOrderedDTO.VariationName == '' || p._PointStoreProductOrderedDTO.VariationName == null) {
                p._PointStoreProductOrderedDTO.VariationName = 'N/A';
              }
              return p;
            });
            return o;
          })
          this.agGrid?.api.updateGridOptions({ rowData: ordersFormatted })
        } else {
          this.agGrid?.api.updateGridOptions({ rowData: changes.orders.currentValue })
        }
      }
    }

    if (changes.headersNames != undefined && changes.headersNames.currentValue != null) {
      if (this.grid) {
        let columnDef = this.grid.columnDefs;

        //get names and visibility for Group Level 2 - 4
        (this.grid.columnDefs[3] as ColDef).hide = changes.headersNames.currentValue[0].HideColumn;
        (this.grid.columnDefs[3] as ColDef).headerName = this.getColumnName(changes.headersNames.currentValue[0].ColumnName, changes.headersNames.currentValue[0].ColumnName);

        (this.grid.columnDefs[4] as ColDef).hide = changes.headersNames.currentValue[1].HideColumn;
        (this.grid.columnDefs[4] as ColDef).headerName = this.getColumnName(changes.headersNames.currentValue[1].ColumnName, changes.headersNames.currentValue[1].ColumnName);

        (this.grid.columnDefs[5] as ColDef).hide = changes.headersNames.currentValue[2].HideColumn;
        (this.grid.columnDefs[5] as ColDef).headerName = this.getColumnName(changes.headersNames.currentValue[2].ColumnName, changes.headersNames.currentValue[2].ColumnName);

        columnDef = columnDef.filter((col: ColDef) => col.headerName && col.headerName.trim() !== "");

        this.agGrid?.api.setColumnDefs([]);
        this.agGrid?.api.setColumnDefs(columnDef);
        this.agGrid?.columnApi.autoSizeAllColumns();
        this.agGrid?.columnApi.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'desc' }] });
      }
    }

    //Determine if overlay should be displayed
    if (this.grid && this.agGrid?.api) {
      let rowCount = this.agGrid?.api.getDisplayedRowCount();
      if (rowCount > 0)
        this.agGrid?.api.hideOverlay();
      else
        this.agGrid?.api.showNoRowsOverlay();
    }

    //Apply filters
    setTimeout(() => {
        if (this.agGrid?.api) {
            this.agGrid.api.setFilterModel(this.currentFilters);
            this.agGrid.api.applyColumnState({
                state: this.currentSort,
                applyOrder: true
            });
            this.agGrid.api.onFilterChanged();
        }
    }, 0);
  }

  /**
   * 
   * @param key 
   * @param defaultValue 
   * @returns 
   */
  getColumnName(key: string, defaultValue: string): string {
    if (this.columnLocalization && this.columnLocalization.has(key))
      return this.columnLocalization.get(key);
    else
      return defaultValue;
  }

  /**
   * 
   * @param status 
   * @returns 
   */
  getStatusName(status: MarketplaceOrderStatus): string {
    // grab localized status name from factory
    let statusInfo = this.pspof.OrderStatus.find((os) => os.value == status);
    if (statusInfo)
      return statusInfo.name;

    // status not found, fallback to switch
    switch (status) {
      case MarketplaceOrderStatus.InTransit:
        return "In Transit";
      case MarketplaceOrderStatus.ReadyForPickup:
        return "Ready for Pickup";
      case MarketplaceOrderStatus.Complete:
        return "Complete";
      case MarketplaceOrderStatus.Shipped:
        return "Shipped";
      case MarketplaceOrderStatus.Cancelled:
        return "Cancelled";
      case MarketplaceOrderStatus.Created:
      case MarketplaceOrderStatus.Processing:
      default:
        return "Processing";
    }
  }

  /**
   * 
   * @param datePosition 
   * @param dateOrdered 
   * @param isYear 
   * @returns 
   */
  daysBetweenDates(datePosition: number, dateOrdered: any, isYear: boolean = false, MSTOffset: number = 0): boolean {
    const now = moment();
    const futureDate = moment(now).add(MSTOffset, 'hours');
    const isSameDayWithMSTOffset: boolean = now.isSame(futureDate, 'day');

    const startDate = moment().subtract((datePosition - (isSameDayWithMSTOffset ? 0 : 1)), 'days');
    const currentDate = moment().add((isSameDayWithMSTOffset ? 0 : 1), 'days');        
    const orderDate = moment(dateOrdered);
    let res = false;
    if (isYear) {
      res = orderDate.year() === datePosition;
    } else {
      res = orderDate.isBetween(startDate, currentDate, 'days', '[]')
    }
    return res;
  }


  /**
   * 
   * @param valueA 
   * @param valueB 
   * @returns number
   */
  statusComparator(valueA, valueB): number {
    const statusA = this.getStatusName(valueA);
    const statusB = this.getStatusName(valueB);
    return (statusA == statusB) ? 0 : (statusA > statusB) ? 1 : -1;
  }


  /**
   * 
   * @param filterDate 
   * @param cellValue 
   * @returns number
   */

  dateComparator(filterDate, cellValue): number {
    if (cellValue == null) {
      return 0;
    }
    var formattedDate = this.datePipe.transform(cellValue, CVAngularDateFormat)
    var dateParts = formattedDate.split('/');
    var year = Number(dateParts[2]);
    var month = Number(dateParts[0]) - 1;
    var day = Number(dateParts[1]);
    var cellDate = new Date(year, month, day);
    if (cellDate < filterDate) {
      return -1;
    } else if (cellDate > filterDate) {
      return 1;
    } else {
      return 0;
    }
  }

  /**
   * 
   * @param event 
   */
  onSortChangedEvent(event): void {
    const sortedColumn = event.columnApi.getColumnState().find(col => Boolean(col.sort));
    if (sortedColumn == null || sortedColumn == undefined) {
      if (!this.isOrderNumberClicked) {
        this.agGrid?.api.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'desc' }] });
        this.orderNumberPreviousSort == 2;
      } else {
        if (this.orderNumberPreviousSort == 0) {
          this.agGrid?.api.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'desc' }] });
          this.orderNumberPreviousSort = 2;
        } else {
          if (this.orderNumberPreviousSort == 2) {
            this.agGrid?.api.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'asc' }] });
            this.orderNumberPreviousSort = 1;
          } else {
            this.agGrid?.api.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'desc' }] });
            this.orderNumberPreviousSort = 2;
          }
        }
      }
    } else {
      if (!this.isOrderNumberClicked) {
        this.orderNumberPreviousSort = 2;
      } else {
        if (sortedColumn.sort == 'asc') {
          this.orderNumberPreviousSort = 2;
        }
      }
    }

    if (sortedColumn != undefined) {
      this.pspof.sortAdminBy = sortedColumn.colId;
      this.pspof.sortAdminReverse = (sortedColumn.sort == "asc") ? false : true;
    }
  }

  /**
   * 
   * @param childrenLength 
   * @returns 
   */
  calculateDynamicHeight(childrenLength): number {
    return this.baseHeightDetailRow + (childrenLength - 1) * this.rowHeightChildDetailRow;
  }

  /**
   * 
   * @param rowNode 
   * @returns 
   */
  getRowDetailNode(rowNode: IRowNode) {
    if (rowNode instanceof RowNode) {
      const rowDetailNode: RowNode = rowNode.detailNode;
      const childrenLength = rowDetailNode.data.ProductsCollection?.length;
      return {
        rowDetailNode, childrenLength
      };
    }
  }

  /**
   * 
   * @param childrenLength 
   * @returns 
   */
  validateChildrensToChangeHeight(childrenLength): boolean {
    return childrenLength <= this.childrensToChangeHeight;
    }

  /**
   * 
   * @params gameType
   * @returns
   */
    getGameName(gameType): string {
      let gameName = '';

      switch (gameType) {
        case 9:
          gameName = this.getColumnName('Raffle', 'Raffle');
          break;
        case 10:
          gameName = this.getColumnName('Bracket_Tournament', 'Brackets Tournament');
          break;
      }
      return gameName;
    }
}

